import React from "react";
import { Link } from "react-router-dom";
import {
  FaArrowLeft,
  FaArrowRight,
  FaFootballBall,
  FaRunning,
  FaBasketballBall,
} from "react-icons/fa";
import { GiWhistle, GiBoxingGloveSurprise } from "react-icons/gi";
import { motion } from "framer-motion";

const activities = [
  {
    title: "Football",
    icon: <FaFootballBall />,
    desc: "Training sessions and inter-school competitions.",
    color: "#22c55e",
  },
  {
    title: "Basketball",
    icon: <FaBasketballBall />,
    desc: "Daily practice, skill drills, and tournaments.",
    color: "#f97316",
  },
  {
    title: "Running & Athletics",
    icon: <FaRunning />,
    desc: "Track and field events, sprint practice, and fitness.",
    color: "#3b82f6",
  },
  {
    title: "Kabaddi",
    icon: <GiWhistle />,
    desc: "Dedicated state and national level kabaddi coaching.",
    color: "#f4c842",
  },
  {
    title: "Wrestling",
    icon: <GiBoxingGloveSurprise />,
    desc: "Traditional wrestling drills, strength and agility training.",
    color: "#ec4899",
  },
  {
    title: "Physical Education",
    icon: <GiWhistle />,
    desc: "Comprehensive physical fitness curriculum and guidance.",
    color: "#06b6d4",
  },
];

const sportsImages = [
  "/renamed_website_images/sports-activities-gallery-01.jpg",
  "/renamed_website_images/sports-activities-kabaddi-2.jpg",
  "/renamed_website_images/sports-activities-kabaddi-3.jpg",
  "/renamed_website_images/sports-activities-kabaddi-4.jpg",
];

const Sports = () => {
  return (
    <section className="relative min-h-screen py-24 px-6 md:px-16 pt-36 md:pt-40 overflow-hidden bg-[#020810]">
      <div
        className="absolute bottom-0 left-0 w-96 h-96 pointer-events-none"
        style={{
          background:
            "radial-gradient(circle, rgba(6,182,212,0.07) 0%, transparent 70%)",
        }}
      />

      <div className="max-w-6xl mx-auto relative z-10">
        <Link
          to="/facilities"
          className="inline-block text-sm font-bold tracking-wide mb-8 transition-colors duration-200"
          style={{ fontFamily: "Nunito, sans-serif", color: "#f4c842" }}
        >
          ← Back to Facilities
        </Link>

        <h1
          className="uppercase mb-4"
          style={{
            fontFamily: "Orbitron, sans-serif",
            fontWeight: 900,
            fontSize: "clamp(1.8rem, 4vw, 3rem)",
            color: "#f0f5ff",
          }}
        >
          Sports Activities
        </h1>
        <div className="cosmic-divider mb-8" />

        {/* National Achievement Highlight */}
        <div className="mb-10 p-6 md:p-8 rounded-2xl bg-gradient-to-r from-gold/15 via-purple-900/20 to-blue-900/20 border border-gold/40 backdrop-blur-md relative overflow-hidden">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6 relative z-10">
            <div className="flex items-center gap-5">
              <div className="w-16 h-16 rounded-2xl bg-gold/20 border border-gold/50 flex items-center justify-center text-gold text-3xl flex-shrink-0 shadow-[0_0_20px_rgba(244,200,66,0.3)]">
                🏆
              </div>
              <div>
                <span className="text-xs font-black uppercase tracking-[0.2em] text-gold block mb-1">
                  National Sports Highlight
                </span>
                <h3 className="text-xl md:text-2xl font-black text-white" style={{ fontFamily: "Orbitron, sans-serif" }}>
                  2 Students Selected for ICSE National Kabaddi Games
                </h3>
                <p className="text-gray-300 text-sm mt-1">
                  Galaxy Star Public School celebrates our proud student athletes representing our institution at the National ICSE Kabaddi Games level!
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Activity grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-12">
          {activities.map((act, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              className="glass-card p-6 text-center transition-all duration-300 hover:scale-[1.03]"
              style={{ borderColor: `${act.color}33` }}
            >
              <div
                className="w-14 h-14 rounded-full mx-auto mb-4 flex items-center justify-center text-2xl"
                style={{
                  background: `${act.color}18`,
                  border: `1px solid ${act.color}44`,
                  color: act.color,
                }}
              >
                {act.icon}
              </div>
              <h4
                className="font-bold mb-2"
                style={{
                  fontFamily: "Cinzel, serif",
                  color: act.color,
                  fontSize: "0.95rem",
                }}
              >
                {act.title}
              </h4>
              <p
                className="text-sm"
                style={{ fontFamily: "Nunito, sans-serif", color: "#8899b8" }}
              >
                {act.desc}
              </p>
            </motion.div>
          ))}
        </div>

        {/* Real Sports Gallery */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-12">
          {sportsImages.map((src, i) => (
            <div
              key={i}
              className="rounded-xl overflow-hidden transition-all duration-300 hover:scale-[1.02]"
              style={{
                border: "1px solid rgba(6,182,212,0.2)",
                boxShadow: "0 8px 32px rgba(0,0,0,0.35)",
              }}
            >
              <img
                src={src}
                alt={`Sports activity ${i + 1}`}
                className="w-full object-cover"
                style={{ height: "180px" }}
              />
            </div>
          ))}
        </div>

        {/* Bottom Navigation Buttons */}
        <div className="flex items-center justify-between pt-6 border-t border-white/10">
          <Link
            to="/facilities/music-room"
            className="flex items-center gap-2 px-5 py-2.5 rounded-full glass-card text-xs uppercase tracking-widest text-gold hover:border-gold transition-all"
          >
            <FaArrowLeft /> Previous: Music Room
          </Link>
          <Link
            to="/facilities/biology-lab"
            className="flex items-center gap-2 px-5 py-2.5 rounded-full cosmic-btn-gold text-xs uppercase tracking-widest"
          >
            Next: Biology Lab <FaArrowRight />
          </Link>
        </div>
      </div>

      {/* Side floating arrows */}
      <Link
        to="/facilities/music-room"
        aria-label="Previous: Music Room"
        className="fixed z-50 left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full items-center justify-center flex transition-all duration-300 shadow-xl"
        style={{
          background: "rgba(2,8,16,0.85)",
          border: "1px solid rgba(244,200,66,0.3)",
          color: "#f4c842",
          backdropFilter: "blur(8px)",
        }}
      >
        <FaArrowLeft />
      </Link>
      <Link
        to="/facilities/biology-lab"
        aria-label="Next: Biology Lab"
        className="fixed z-50 right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full items-center justify-center flex transition-all duration-300 shadow-xl"
        style={{
          background: "rgba(2,8,16,0.85)",
          border: "1px solid rgba(244,200,66,0.3)",
          color: "#f4c842",
          backdropFilter: "blur(8px)",
        }}
      >
        <FaArrowRight />
      </Link>
    </section>
  );
};

export default Sports;
